import { useMutation, useQuery } from '@apollo/client';
import { Exchanges } from '@interfaces/financialProduct';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FETCH_TRADE_REQUESTS_FOR_BATCH } from '../queries';
import {
  ALIGNED_TABLE_SX,
  BOLD_SX,
  BORDERED_BOX_SX,
  CARD_HEADER_SX,
  FILLS_FULL_WIDTH_SX,
  LABEL_SX,
  MICRO_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  SMALL_BOLD_SX,
  SMALL_SX,
  TABULAR_SX,
  VALUE_SX,
} from '../styles';
import {
  type BulkTradeViewKey,
  formatMoney,
  getActiveStep,
  getStepIndex,
  tradeSideColor,
} from '../utils';
import { STEPS, TRANSITION_LABELS } from '../utils/constants';
import {
  REMOVE_TRADE_REQUEST,
  TRANSITION_BULK_TRADE_RUN,
} from '../utils/mutations';
import {
  LegacyBulkTradeRunAllocationsTable,
  LegacyUpdateMutualFundTradesTable,
  LegacyUpdateTradesTable,
} from './legacyTableAdapters';

type BulkTradeRunInlineWorkflowProps = {
  operation: any;
  onRefresh: () => void;
  parentPolling?: boolean;
};

const FileDownloadChip = ({
  label,
  onClick,
  color,
  hoverColor,
  sys,
}: {
  label: string;
  onClick: () => void;
  color?: string;
  hoverColor?: string;
  sys: any;
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.25,
      px: 0.75,
      py: 0.25,
      ...BORDERED_BOX_SX,
      borderRadius: 0.5,
      cursor: 'pointer',
      '&:hover': {
        borderColor: hoverColor ?? sys.color.primary,
        bgcolor: sys.color.neutralHover,
      },
    }}
  >
    <DownloadIcon
      sx={{ fontSize: 12, color: color ?? sys.color.onSurfaceVariant }}
    />
    <Typography
      variant='labelSmall'
      sx={{
        ...MICRO_SX,
        fontWeight: 600,
        color: color ?? sys.color.onSurfaceVariant,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const InlineTradeReviewTable = ({
  bulkTradeRunId,
  onRemoved,
}: {
  bulkTradeRunId: string;
  onRemoved: () => void;
}) => {
  const { sys } = useThemeTokens();
  const { data, loading, refetch } = useQuery(FETCH_TRADE_REQUESTS_FOR_BATCH, {
    variables: {
      input: {
        filter: { bulkTradeRunId },
        pagination: {
          sortField: 'account',
          sortDesc: false,
          perPage: 500,
          offSet: 0,
        },
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const [removeTradeRequest, { loading: removing }] = useMutation(
    REMOVE_TRADE_REQUEST,
    {
      onCompleted: () => {
        refetch();
        onRemoved();
      },
      onError: (err) => {
        console.error('Failed to remove trade request:', err);
      },
    },
  );
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const trades = data?.fetchTradeRequests?.tradeRequests || [];
  const serverTotalCount = data?.fetchTradeRequests?.totalCount;

  const summary = useMemo(() => {
    let buys = 0,
      sells = 0,
      buyValue = 0,
      sellValue = 0;
    for (const t of trades) {
      const val = t.moneyAllocatedCents || 0;
      if (t.type === 'BUY') {
        buys++;
        buyValue += val;
      } else {
        sells++;
        sellValue += val;
      }
    }
    const total = serverTotalCount ?? trades.length;
    return { buys, sells, buyValue, sellValue, total };
  }, [trades, serverTotalCount]);

  if (loading && trades.length === 0)
    return (
      <Box display='flex' justifyContent='center' py={3}>
        <CircularProgress size={20} />
      </Box>
    );
  if (trades.length === 0)
    return (
      <Box py={2} textAlign='center'>
        <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
          No trade requests in this batch
        </Typography>
      </Box>
    );

  return (
    <Box>
      <Box
        display='flex'
        gap={2.5}
        px={2}
        py={0.75}
        sx={{
          borderBottom: '1px solid',
          borderColor: sys.color.outlineVariant,
          bgcolor: sys.color.background,
        }}
      >
        {[
          {
            label: 'BUYS',
            value: String(summary.buys),
            sub: formatMoney(summary.buyValue),
            color: sys.color.positive,
          },
          {
            label: 'SELLS',
            value: String(summary.sells),
            sub: formatMoney(summary.sellValue),
            color: sys.color.negative,
          },
          {
            label: 'TOTAL',
            value: String(summary.total),
            color: sys.color.onSurface,
          },
        ].map((m) => (
          <Box key={m.label} display='flex' alignItems='baseline' gap={0.5}>
            <Typography variant='labelSmall' sx={{ ...SECTION_HEADER_SX }}>
              {m.label}
            </Typography>
            <Typography
              variant='bodySmall'
              weight='bold'
              sx={{ ...TABULAR_SX, color: m.color }}
            >
              {m.value}
            </Typography>
            {m.sub && (
              <Typography
                variant='labelSmall'
                sx={{ ...TABULAR_SX, color: m.color }}
              >
                ({m.sub})
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ maxHeight: 380, overflowY: 'auto', overflowX: 'auto' }}>
        <Table sx={{ ...ALIGNED_TABLE_SX, tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              {[
                { label: 'Client', right: false },
                { label: 'Account', right: false },
                { label: 'Security', right: false },
                { label: 'Side', right: false },
                { label: 'Ccy', right: false },
                { label: 'Qty', right: true },
                { label: 'Value', right: true },
                { label: 'State', right: true },
                { label: '', right: false },
              ].map((col) => (
                <TableHeadCell
                  key={col.label || '_remove'}
                  right={col.right}
                  sx={{
                    padding: '6px 16px',
                    ...(col.label === ''
                      ? { width: 32, padding: '6px 4px' }
                      : {}),
                  }}
                >
                  {col.label}
                </TableHeadCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {trades.map((trade: any) => {
              const clientName =
                trade.account?.user?.entityName ||
                [trade.account?.user?.firstName, trade.account?.user?.lastName]
                  .filter(Boolean)
                  .join(' ') ||
                '—';
              const isBuy = trade.type === 'BUY';
              const sideColor = tradeSideColor(sys.color, isBuy);
              const isRemovable =
                trade.state === 'INITIATED' && !trade.hasPairs;
              const isConfirming = confirmRemoveId === trade.id;
              const stateColor =
                trade.state === 'RECONCILED'
                  ? sys.color.positive
                  : trade.state === 'CANCELED' || trade.state === 'FAILED'
                    ? sys.color.negative
                    : sys.color.warning;

              return (
                <TableRow key={trade.id} hover>
                  <TableCell
                    dense
                    bold
                    sx={{
                      padding: '4px 16px',
                      maxWidth: '160px',
                      borderLeft: '3px solid',
                      borderLeftColor: sideColor,
                    }}
                  >
                    {clientName}
                  </TableCell>
                  <TableCell dense sx={{ padding: '4px 16px' }}>
                    {trade.account?.type || '—'}
                  </TableCell>
                  <TableCell dense bold sx={{ padding: '4px 16px' }}>
                    {trade.financialProduct?.ticker || '—'}
                  </TableCell>
                  <TableCell
                    dense
                    sx={{
                      padding: '4px 16px',
                      color: isBuy ? sys.color.positive : sys.color.negative,
                      fontWeight: 700,
                    }}
                  >
                    {trade.type}
                  </TableCell>
                  <TableCell dense sx={{ padding: '4px 16px' }}>
                    {trade.currency}
                  </TableCell>
                  <TableCell dense number sx={{ padding: '4px 16px' }}>
                    {trade.expectedQuantity != null
                      ? Number(trade.expectedQuantity).toFixed(4)
                      : '—'}
                  </TableCell>
                  <TableCell
                    dense
                    number
                    sx={{
                      padding: '4px 16px',
                      color: sideColor,
                      fontWeight: 600,
                    }}
                  >
                    {formatMoney(trade.moneyAllocatedCents || 0)}
                  </TableCell>
                  <TableCell
                    dense
                    right
                    sx={{
                      padding: '4px 16px',
                      color: stateColor,
                      fontWeight: 700,
                    }}
                  >
                    {trade.state}
                  </TableCell>
                  <TableCell dense sx={{ padding: '4px 4px', width: 32 }}>
                    {isRemovable && !isConfirming && (
                      <Typography
                        variant='labelSmall'
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmRemoveId(trade.id);
                        }}
                        sx={{
                          ...VALUE_SX,
                          cursor: 'pointer',
                          color: sys.color.onDisabled,
                          lineHeight: 1,
                          textAlign: 'center',
                          '&:hover': { color: sys.color.negative },
                        }}
                      >
                        ✕
                      </Typography>
                    )}
                    {isConfirming && (
                      <Box display='flex' gap={0.5} justifyContent='center'>
                        <Typography
                          variant='labelSmall'
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTradeRequest({
                              variables: { tradeRequestId: trade.id },
                            });
                            setConfirmRemoveId(null);
                          }}
                          sx={{
                            cursor: removing ? 'wait' : 'pointer',
                            color: sys.color.negative,
                            ...BOLD_SX,
                          }}
                        >
                          Yes
                        </Typography>
                        <Typography
                          variant='labelSmall'
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmRemoveId(null);
                          }}
                          sx={{ cursor: 'pointer', ...SECONDARY_TEXT_SX }}
                        >
                          No
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

const BulkTradeRunInlineWorkflow = ({
  operation,
  onRefresh,
  parentPolling = false,
}: BulkTradeRunInlineWorkflowProps) => {
  const { sys } = useThemeTokens();
  const { showToast } = useGlobalToast();
  const [activeView, setActiveView] = useState<BulkTradeViewKey>('review');
  const [buyTradesWarnings, setBuyTradesWarnings] = useState(false);
  const [sellTradesWarnings, setSellTradesWarnings] = useState(false);
  const [tradesSaved, setTradesSaved] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [fillsTab, setFillsTab] = useState<
    'buyTrades' | 'sellTrades' | 'buyMF' | 'sellMF'
  >('buyTrades');

  const [transitionBulkTradeRun, { loading: transitionLoading }] = useMutation(
    TRANSITION_BULK_TRADE_RUN,
    {
      onCompleted: () => onRefresh(),
      onError: (err) => {
        showToast({
          severity: 'error',
          message: err?.message || 'Transition failed',
        });
      },
    },
  );

  useEffect(() => {
    setActiveView(getActiveStep(operation.state));
  }, [operation.state]);
  useEffect(() => {
    if (operation.incompleteTradeRequestsByExchange)
      setTradesSaved(operation.incompleteTradeRequestsByExchange.length === 0);
  }, [operation.incompleteTradeRequestsByExchange]);

  const fillsTabs = useMemo(() => {
    const btr = operation.bulkTradeRequests || [];
    const mf = operation.bulkMutualFundTrades || [];
    const buyTrades =
      btr.find((r: any) => r.type === 'BUY')?.bulkTrades?.length || 0;
    const sellTrades =
      btr.find((r: any) => r.type === 'SELL')?.bulkTrades?.length || 0;
    const buyMF = mf.filter((t: any) => t.type === 'BUY').length;
    const sellMF = mf.filter((t: any) => t.type === 'SELL').length;

    const posColor = sys.color.positive || '#4CAF50';
    const negColor = sys.color.negative || '#F44336';

    const tabs: {
      key: 'buyTrades' | 'sellTrades' | 'buyMF' | 'sellMF';
      label: string;
      color: string;
      count: number;
    }[] = [];
    if (buyTrades > 0)
      tabs.push({
        key: 'buyTrades',
        label: 'Buy Trades',
        color: posColor,
        count: buyTrades,
      });
    if (sellTrades > 0)
      tabs.push({
        key: 'sellTrades',
        label: 'Sell Trades',
        color: negColor,
        count: sellTrades,
      });
    if (buyMF > 0)
      tabs.push({
        key: 'buyMF',
        label: 'Buy Mutual Funds',
        color: posColor,
        count: buyMF,
      });
    if (sellMF > 0)
      tabs.push({
        key: 'sellMF',
        label: 'Sell Mutual Funds',
        color: negColor,
        count: sellMF,
      });

    return tabs.length > 0
      ? tabs
      : [
          {
            key: 'buyTrades' as const,
            label: 'Buy Trades',
            color: posColor,
            count: 0,
          },
          {
            key: 'sellTrades' as const,
            label: 'Sell Trades',
            color: negColor,
            count: 0,
          },
        ];
  }, [
    operation.bulkTradeRequests,
    operation.bulkMutualFundTrades,
    sys.color.positive,
    sys.color.negative,
  ]);

  useEffect(() => {
    if (fillsTabs.length > 0 && !fillsTabs.some((t) => t.key === fillsTab)) {
      setFillsTab(fillsTabs[0].key);
    }
  }, [fillsTabs, fillsTab]);

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (parentPolling) return;
    const terminal = [
      'COMPLETED',
      'COMPLETED_PENDING_PTFS',
      'CANCELED',
    ].includes(operation.state);
    const stale =
      !terminal &&
      Date.now() - new Date(operation.createdAt).getTime() >
        24 * 60 * 60 * 1000;

    if (!terminal && !stale) {
      pollRef.current = setInterval(() => onRefreshRef.current(), 3_000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [operation.state, operation.createdAt, parentPolling]);

  const handleTransition = async (
    transition:
      | 'generateBulkTradeFiles'
      | 'resetBulkTrades'
      | 'cancel'
      | 'initiateAllocations',
  ) => {
    try {
      await transitionBulkTradeRun({
        variables: { input: { bulkTradeRunId: operation.id, transition } },
      });
      showToast({
        severity: 'success',
        message: TRANSITION_LABELS[transition] || 'Transition complete',
      });
      setConfirmCancel(false);
    } catch {
      // Error handled by onError callback
    }
  };

  const state = operation.state;
  const isProcessing = [
    'GENERATING',
    'BULK_TRADES_FILES_GENERATING',
    'BULK_TRADES_REQUESTED',
    'ALLOCATIONS_REQUESTED',
  ].includes(state);
  const isReviewStage = state === 'BULK_TRADES_INITIATED';
  const isUpdateStage =
    state === 'BULK_TRADES_COMPLETED' || state === 'COMPLETED_PENDING_PTFS';
  const isAllocationStage = state === 'ALLOCATIONS_COMPLETED';
  const isComplete = state === 'COMPLETED';
  const currentStepIdx = getStepIndex(getActiveStep(state));

  return (
    <Box>
      {operation.updatedAt && (
        <Box display='flex' justifyContent='flex-end' mb={0.5}>
          <Typography
            variant='labelSmall'
            sx={{ ...SMALL_SX, color: sys.color.onSurfaceVariant }}
          >
            Last updated: {new Date(operation.updatedAt).toLocaleString()}
          </Typography>
        </Box>
      )}
      <Box
        display='flex'
        alignItems='center'
        mb={2}
        px={0.5}
        py={0.75}
        sx={{ bgcolor: sys.color.background, ...BORDERED_BOX_SX }}
      >
        {STEPS.map((step, idx) => {
          const isCurrent = activeView === step.key;
          const isPast = idx < currentStepIdx;
          const isClickable =
            (step.key === 'review' &&
              (isReviewStage ||
                isUpdateStage ||
                isAllocationStage ||
                isComplete)) ||
            (step.key === 'update' &&
              (isUpdateStage || isAllocationStage || isComplete)) ||
            (step.key === 'allocations' && (isAllocationStage || isComplete)) ||
            (step.key === 'complete' && isComplete);

          return (
            <Box key={step.key} display='flex' alignItems='center' flex={1}>
              <Box
                onClick={
                  isClickable ? () => setActiveView(step.key) : undefined
                }
                display='flex'
                alignItems='center'
                gap={0.75}
                sx={{
                  cursor: isClickable ? 'pointer' : 'default',
                  opacity: isClickable || isCurrent ? 1 : 0.4,
                  '&:hover': isClickable ? { opacity: 0.8 } : {},
                  flex: 1,
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...SMALL_SX,
                    fontWeight: 800,
                    bgcolor: isPast
                      ? sys.color.positive
                      : isCurrent
                        ? sys.color.primary
                        : sys.color.disabled,
                    color:
                      isPast || isCurrent
                        ? sys.color.onPrimary
                        : sys.color.onDisabled,
                    transition: 'all 0.15s',
                  }}
                >
                  {isPast ? (
                    <CheckCircleIcon sx={{ fontSize: 14, display: 'block' }} />
                  ) : (
                    step.num
                  )}
                </Box>
                <Typography
                  variant='labelSmall'
                  sx={{
                    ...LABEL_SX,
                    fontWeight: isCurrent ? 700 : 500,
                    color: isCurrent
                      ? sys.color.primary
                      : isPast
                        ? sys.color.positive
                        : sys.color.onSurfaceVariant,
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
              {idx < STEPS.length - 1 && (
                <Box
                  sx={{
                    width: 24,
                    height: 1,
                    bgcolor:
                      idx < currentStepIdx
                        ? sys.color.positive
                        : sys.color.outlineVariant,
                    mx: 0.5,
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {isProcessing && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          gap={1.5}
          py={4}
        >
          <CircularProgress size={20} />
          <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
            {state === 'GENERATING' && 'Generating trade run...'}
            {state === 'BULK_TRADES_FILES_GENERATING' &&
              'Generating trade files...'}
            {state === 'BULK_TRADES_REQUESTED' && 'Processing trades...'}
            {state === 'ALLOCATIONS_REQUESTED' && 'Processing allocations...'}
          </Typography>
        </Box>
      )}

      {activeView === 'review' && isReviewStage && (
        <Box>
          <Box sx={{ ...CARD_HEADER_SX, borderBottom: 'none', mb: 1.5 }}>
            <Typography variant='bodySmall' weight='bold'>
              Review Generated Trades
            </Typography>
            <Typography
              variant='labelSmall'
              sx={{ ...SECONDARY_TEXT_SX, ...SMALL_SX }}
            >
              Verify all trades before generating files
            </Typography>
          </Box>
          <Box sx={{ ...BORDERED_BOX_SX, overflow: 'hidden', mb: 2 }}>
            <InlineTradeReviewTable
              bulkTradeRunId={operation.id}
              onRemoved={onRefresh}
            />
          </Box>
          <Box sx={{ ...CARD_HEADER_SX, borderBottom: 'none' }}>
            {!confirmCancel ? (
              <Typography
                variant='labelSmall'
                onClick={() => setConfirmCancel(true)}
                sx={{
                  ...LABEL_SX,
                  color: sys.color.onDisabled,
                  cursor: 'pointer',
                  '&:hover': { color: sys.color.negative },
                  transition: 'color 0.1s',
                }}
              >
                Cancel Trade Run
              </Typography>
            ) : (
              <Box display='flex' alignItems='center' gap={1}>
                <Typography
                  variant='labelSmall'
                  sx={{ ...LABEL_SX, color: sys.color.negative }}
                >
                  Cancel this run?
                </Typography>
                <Typography
                  variant='labelSmall'
                  onClick={() => handleTransition('cancel')}
                  sx={{
                    ...LABEL_SX,
                    ...BOLD_SX,
                    color: sys.color.negative,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Yes, cancel
                </Typography>
                <Typography
                  variant='labelSmall'
                  onClick={() => setConfirmCancel(false)}
                  sx={{
                    ...SECONDARY_TEXT_SX,
                    ...LABEL_SX,
                    cursor: 'pointer',
                  }}
                >
                  No
                </Typography>
              </Box>
            )}
            <Button
              label='Generate Trade Files'
              variant='filled'
              size='sm'
              onClick={() => handleTransition('generateBulkTradeFiles')}
              disabled={transitionLoading}
            />
          </Box>
        </Box>
      )}

      {activeView === 'update' && isUpdateStage && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography variant='bodySmall' weight='bold'>
              Update Prices & Quantities
            </Typography>
            <Box display='flex' alignItems='center' gap={1.5}>
              {(buyTradesWarnings || sellTradesWarnings) && (
                <Box display='flex' alignItems='center' gap={0.5}>
                  <WarningIcon
                    sx={{ color: sys.color.warning, fontSize: 13 }}
                  />
                  <Typography
                    variant='labelSmall'
                    sx={{ ...SMALL_BOLD_SX, color: sys.color.warning }}
                  >
                    Discrepancies detected
                  </Typography>
                </Box>
              )}
              <Typography
                variant='labelSmall'
                sx={{
                  ...SMALL_SX,
                  ...BOLD_SX,
                  color: tradesSaved ? sys.color.positive : sys.color.warning,
                }}
              >
                {tradesSaved ? 'All trades saved' : 'Unsaved changes'}
              </Typography>
            </Box>
          </Box>

          {/* File download chips */}
          {(operation.bulkTradeFiles?.length > 0 ||
            operation.buyBulkTradeFileUrl ||
            operation.sellBulkTradeFileUrl ||
            operation.mutualFundFileUrl) && (
            <Box display='flex' flexWrap='wrap' gap={0.75} mb={1.5}>
              <Typography
                variant='labelSmall'
                sx={{
                  ...SECTION_HEADER_SX,
                  ...MICRO_SX,
                  alignSelf: 'center',
                  mr: 0.5,
                }}
              >
                FILES
              </Typography>
              {operation.bulkTradeFiles?.map((file: any, idx: number) => (
                <FileDownloadChip
                  key={idx}
                  label={file.requestType || file.type}
                  onClick={() => window.open(file.url, '_blank')}
                  sys={sys}
                />
              ))}
              {operation.buyBulkTradeFileUrl && (
                <FileDownloadChip
                  label='Buy'
                  onClick={() =>
                    window.open(operation.buyBulkTradeFileUrl, '_blank')
                  }
                  color={sys.color.positive}
                  hoverColor={sys.color.positive}
                  sys={sys}
                />
              )}
              {operation.sellBulkTradeFileUrl && (
                <FileDownloadChip
                  label='Sell'
                  onClick={() =>
                    window.open(operation.sellBulkTradeFileUrl, '_blank')
                  }
                  color={sys.color.negative}
                  hoverColor={sys.color.negative}
                  sys={sys}
                />
              )}
              {operation.mutualFundFileUrl && (
                <FileDownloadChip
                  label='Mutual Funds'
                  onClick={() =>
                    window.open(operation.mutualFundFileUrl, '_blank')
                  }
                  sys={sys}
                />
              )}
            </Box>
          )}

          {/* Pill tab navigation — only shows tabs that have trades */}
          <Box display='flex' gap={0.5} mb={1.5}>
            {fillsTabs.map((tab) => {
              const isActive = fillsTab === tab.key;
              return (
                <Box
                  key={tab.key}
                  onClick={() => setFillsTab(tab.key)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    ...VALUE_SX,
                    fontWeight: isActive ? 700 : 500,
                    letterSpacing: '0.02em',
                    color: isActive ? '#fff' : sys.color.onSurfaceVariant,
                    bgcolor: isActive
                      ? tab.color || sys.color.primary
                      : sys.color.neutralHover,
                    border: '1px solid',
                    borderColor: isActive
                      ? 'transparent'
                      : sys.color.outlineVariant,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: isActive
                        ? tab.color || sys.color.primary
                        : sys.color.outline,
                    },
                    userSelect: 'none',
                  }}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <Box
                      component='span'
                      sx={{
                        ...SMALL_BOLD_SX,
                        fontWeight: 700,
                        bgcolor: isActive
                          ? 'rgba(255,255,255,0.25)'
                          : sys.color.outlineVariant,
                        color: isActive ? '#fff' : sys.color.onSurfaceVariant,
                        borderRadius: '8px',
                        px: 0.75,
                        py: '1px',
                        lineHeight: 1.3,
                        minWidth: 18,
                        textAlign: 'center',
                      }}
                    >
                      {tab.count}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Full-width active panel */}
          <Box
            sx={{
              ...FILLS_FULL_WIDTH_SX(
                (fillsTab === 'buyTrades' || fillsTab === 'buyMF'
                  ? sys.color.positive
                  : sys.color.negative) ?? '#888',
                sys,
              ),
              maxHeight: 480,
              overflowY: 'auto',
            }}
          >
            {fillsTab === 'buyTrades' && (
              <LegacyUpdateTradesTable
                bulkTradeRunId={operation.id}
                type='BUY'
                setDiscrepancyWarning={setBuyTradesWarnings}
                exchanges={
                  state === 'COMPLETED_PENDING_PTFS' ? [Exchanges.PTF] : []
                }
                onPriceUpdate={onRefresh}
              />
            )}
            {fillsTab === 'sellTrades' && (
              <LegacyUpdateTradesTable
                bulkTradeRunId={operation.id}
                type='SELL'
                setDiscrepancyWarning={setSellTradesWarnings}
                exchanges={
                  state === 'COMPLETED_PENDING_PTFS' ? [Exchanges.PTF] : []
                }
                onPriceUpdate={onRefresh}
              />
            )}
            {fillsTab === 'buyMF' && (
              <LegacyUpdateMutualFundTradesTable
                bulkTradeRunId={operation.id}
                type='BUY'
              />
            )}
            {fillsTab === 'sellMF' && (
              <LegacyUpdateMutualFundTradesTable
                bulkTradeRunId={operation.id}
                type='SELL'
              />
            )}
          </Box>

          {/* Footer actions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 2,
            }}
          >
            <Box display='flex' gap={1} alignItems='center'>
              {state === 'BULK_TRADES_COMPLETED' && (
                <>
                  <Typography
                    variant='labelSmall'
                    onClick={() => handleTransition('cancel')}
                    sx={{
                      ...LABEL_SX,
                      color: sys.color.onDisabled,
                      cursor: 'pointer',
                      '&:hover': { color: sys.color.negative },
                    }}
                  >
                    Cancel
                  </Typography>
                  <Typography
                    variant='labelSmall'
                    sx={{ color: sys.color.outlineVariant }}
                  >
                    |
                  </Typography>
                  <Typography
                    variant='labelSmall'
                    onClick={() => handleTransition('resetBulkTrades')}
                    sx={{
                      ...LABEL_SX,
                      color: sys.color.onDisabled,
                      cursor: 'pointer',
                      '&:hover': { color: sys.color.onSurface },
                    }}
                  >
                    Back to Review
                  </Typography>
                </>
              )}
            </Box>
            <Button
              label='Proceed to Allocations'
              variant='filled'
              size='sm'
              onClick={() => handleTransition('initiateAllocations')}
              disabled={!tradesSaved || transitionLoading}
            />
          </Box>
        </Box>
      )}

      {activeView === 'allocations' && isAllocationStage && (
        <Box>
          <Box sx={{ ...CARD_HEADER_SX, borderBottom: 'none', mb: 1.5 }}>
            <Typography variant='bodySmall' weight='bold'>
              Trade Allocations
            </Typography>
          </Box>

          {operation.allocationFiles?.length > 0 && (
            <Box display='flex' flexWrap='wrap' gap={0.75} mb={2}>
              <Typography
                variant='labelSmall'
                sx={{
                  ...SECTION_HEADER_SX,
                  ...MICRO_SX,
                  alignSelf: 'center',
                  mr: 0.5,
                }}
              >
                FILES
              </Typography>
              {operation.allocationFiles?.map((file: any, idx: number) => (
                <Box
                  key={idx}
                  onClick={() => window.open(file.url, '_blank')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    border: '1px solid',
                    borderColor: sys.color.outlineVariant,
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: sys.color.primary,
                      bgcolor: sys.color.neutralHover,
                    },
                  }}
                >
                  <DownloadIcon
                    sx={{ fontSize: 12, color: sys.color.onSurfaceVariant }}
                  />
                  <Typography
                    variant='labelSmall'
                    sx={{ ...MICRO_SX, fontWeight: 600 }}
                  >
                    {file.requestType || file.type}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box
            sx={{
              maxHeight: 380,
              overflowY: 'auto',
              ...BORDERED_BOX_SX,
              borderRadius: 1,
            }}
          >
            <LegacyBulkTradeRunAllocationsTable
              filter={{ bulkTradeRunId: operation.id }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              mt: 1.5,
            }}
          >
            <Box display='flex' alignItems='center' gap={0.5}>
              <CheckCircleIcon
                sx={{ fontSize: 14, color: sys.color.positive }}
              />
              <Typography
                variant='labelSmall'
                sx={{ ...LABEL_SX, color: sys.color.positive }}
              >
                Allocations ready
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {activeView === 'complete' && isComplete && (
        <Box py={3} textAlign='center'>
          <CheckCircleIcon
            sx={{ fontSize: 36, color: sys.color.positive, mb: 1 }}
          />
          <Typography variant='bodyMedium' weight='bold' sx={{ mb: 0.5 }}>
            Trade Run Complete
          </Typography>
          <Typography
            variant='bodySmall'
            sx={{ color: sys.color.onSurfaceVariant, mb: 2 }}
          >
            All trades processed and allocated.
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              gap: 3,
              px: 3,
              py: 1.5,
              bgcolor: sys.color.background,
              ...BORDERED_BOX_SX,
              borderRadius: 1,
            }}
          >
            <Box>
              <Typography
                variant='labelSmall'
                sx={{ ...SECONDARY_TEXT_SX, ...MICRO_SX }}
              >
                EXCHANGES
              </Typography>
              <Typography variant='bodySmall' weight='bold'>
                {operation.allTradeRequestsByExchange?.length || 0}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant='labelSmall'
                sx={{ ...SECONDARY_TEXT_SX, ...MICRO_SX }}
              >
                FILES
              </Typography>
              <Typography variant='bodySmall' weight='bold'>
                {(operation.bulkTradeFiles?.length || 0) +
                  (operation.allocationFiles?.length || 0)}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant='labelSmall'
                sx={{ ...SECONDARY_TEXT_SX, ...MICRO_SX }}
              >
                STATUS
              </Typography>
              <Typography
                variant='bodySmall'
                weight='bold'
                sx={{ color: sys.color.positive }}
              >
                Complete
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BulkTradeRunInlineWorkflow;
